/**
 * Name a new role.
 *
 * Only a name is asked for, because a role with no policy is the honest
 * starting point: it holds nothing until somebody says what it may reach, and
 * the wizard opens on it straight after so that is the next thing that
 * happens rather than something to remember.
 */
import { useState } from 'react';
import Sheet, { Group, Field } from '../Sheet.jsx';

/* users.role is varchar(20). A longer name would be truncated on the way in
   and stop matching the policy written against it. */
const MAX = 20;

export default function NewRoleSheet({ existing = [], onClose, onSaved }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    const next = name.trim();
    if (!next) { setError('Give the role a name'); return; }
    if (next.length > MAX) { setError(`Keep it to ${MAX} characters`); return; }
    if (existing.some(r => r.toLowerCase() === next.toLowerCase())) {
      setError('There is already a role with that name');
      return;
    }

    setBusy(true);
    setError('');
    try {
      await onSaved(next);
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not add the role');
      setBusy(false);
    }
  };

  const left = MAX - name.length;

  return (
    <Sheet
      icon="users"
      tone="g-purple"
      title="Add a role"
      onClose={onClose}
      onSave={busy ? undefined : save}
      saveLabel={busy ? 'Adding…' : 'Add role'}
    >
      <Group>Role</Group>

      <Field label="Name" wide>
        <input
          autoFocus
          maxLength={MAX}
          placeholder="Support Lead"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
        />
        <div className="muted">
          It starts with no access at all — you choose what it may reach next.
          {name ? ` ${left} character${left === 1 ? '' : 's'} left.` : ''}
        </div>
      </Field>

      {error ? <div className="f wide"><div className="err">{error}</div></div> : null}
    </Sheet>
  );
}
